import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { use } from 'react';
import { setFaviconBadge } from '../utils/favicon';


// Create the context
const TelephonyContext = createContext();

// Custom hook to use the context
export const useTelephony = () => {
  const context = useContext(TelephonyContext);
  if (!context) {
    throw new Error('useTelephony must be used within a TelephonyProvider');
  }
  return context;
};

const sanitazeNumber = (number) => {
  if (!number) return '';

  // Preserve * or # if it's the first character, remove all other non-digit characters
  const firstChar = number.charAt(0);
  if (firstChar === '*' || firstChar === '#') {
    return firstChar + number.slice(1).replace(/\D/g, '');
  }

  // Remove all non-digit characters
  return number.replace(/\D/g, '');
}

// Provider component
export const TelephonyProvider = ({ children }) => {
  const [phoneSignal, setPhoneSignal] = useState(null);
  const [dialNumber, setDialNumber] = useState('');
  const [uniqueid, setUniqueid] = useState(null);
  const [callDirection, setCallDirection] = useState(null); // 'inbound' or 'outbound'
  const [callStatus, setCallStatus] = useState({
    status: 'idle',
    timestamp: Date.now(),
  }); // idle, dialing, ringing, connected, ended
  const [sipSignal, setSipSignal] = useState(null);
  const [widgetSignal, setWidgetSignal] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [volume, setVolume] = useState(1);     // 0..1
  const [muted, setMuted] = useState(false);
  const [incomingData, setIncomingData] = useState(null);
  const [ctiData, setCtiData] = useState(null);

  useEffect(() => {
    setFaviconBadge('call', callStatus?.status === 'ringing');
  }, [callStatus?.status]);

  // Function to initiate a dial action
  const initiateCall = useCallback((number) => {
    const sanitazed = sanitazeNumber(number);
    if (!sanitazed) {
      console.warn('TelephonyContext: initiateCall called with invalid number:', number);
      return;
    }
    const signal = {
        action: 'dial',
        number: sanitazed,
        timestamp: Date.now() // Ensure it's a new object each time
    };
    setPhoneSignal(signal);
    setDialNumber(sanitazed);
    setCallStatus({
      status: 'dialing',
      timestamp: Date.now()
    });
  }, []);

  // Function to end a call
  const endCall = useCallback(() => {
    setPhoneSignal({
      action: 'hangup',
      timestamp: Date.now()
    });
    console.log('TelephonyContext: Ending call, setting status to idle.');
    setCallStatus({
      status: 'idle',
      timestamp: Date.now()
    });
    setDialNumber('');
    setUniqueid(null);
    setCallDirection(null);
    setIncomingData(null);
    setCtiData(null);
  }, []);

  // Function to answer a call
  const answerCall = useCallback((number) => {
    setPhoneSignal({
      action: 'answer',
      timestamp: Date.now()
    });
    setDialNumber(number);
    setCallStatus({
      status: 'connected',
      timestamp: Date.now()
    });
  }, []);

  // Function to update call status
  const updateCallStatus = useCallback((status, telephonyTab) => {
    setCallStatus((prevCallStatus) => {
      if (prevCallStatus.status === status) {
        console.log('TelephonyContext: Call status is already', status, ', no update needed.');
        return prevCallStatus; // Return same object to prevent re-render
      }
      return {
        status: status,
        timestamp: Date.now()
      };
    });
    if (status === 'idle') {
      setIncomingData(null);
      setCtiData(null);
      setSipSignal({
        action: 'callEnded',
        timestamp: Date.now()
      });
      if (telephonyTab !== undefined && telephonyTab === false) {
        const signal = {
          action: 'idle',
          timestamp: Date.now()
        };
        setWidgetSignal(signal);
      }
    }
  }, []);

  // Function to clear signals (useful for one-time actions)
  const clearSignal = useCallback(() => {
    setPhoneSignal(null);
  }, []);
  const clearSipSignal = useCallback(() => {
    setSipSignal(null);
  }, []);
  
  const incomingCall = useCallback((number, info, telephonyTab) => {
    const timestamp = Date.now();
    const signal = {
      action: 'incoming',
      number: number,
      info: info,
      timestamp
    };

    // Keep the current incoming call in a single shared state. The widget and
    // the telephony tab can be mounted/unmounted while the phone is ringing,
    // so relying only on sipSignal/widgetSignal leaves each surface with a
    // partial view of the call.
    setIncomingData({ number, info, timestamp });
    if (info?.uniqueid) {
      setUniqueid(info.uniqueid);
    }
    setCallDirection(info?.callType || 'INBOUND');
    setCallStatus({
      status: 'ringing',
      timestamp
    });

    if (telephonyTab) {
      setSipSignal(signal);
    } else {
      setWidgetSignal(signal);
    }
  }, []);


  const setCallCti = useCallback((cti, queue, clientCode, telephonyTab) => {
    const signal = {
      action: 'cti',
      cti: cti,
      queue: queue,
      clientCode: clientCode,
      timestamp: Date.now()
    } ;
    if (telephonyTab) {
      setSipSignal(signal);
    } else {
      setWidgetSignal(signal);
    }
  }, []);

  const sendDTMF = useCallback((digit) => {
    setPhoneSignal({
      action: 'dtmf',
      digit: digit,
      timestamp: Date.now()
    });
  }
  , []);

  const hold = useCallback(() => {
    setPhoneSignal({
      action: 'hold',
      timestamp: Date.now()
    });
  }
  , []);

  const unhold = useCallback(() => {
    setPhoneSignal({
      action: 'unhold',
      timestamp: Date.now()
    });
  }
  , []);

  const toggleMute = useCallback(() => setMuted(m => !m), []);

  const callContact = useCallback((phone_number) => {
    const telephone_item = document.getElementById('webphone-icon')
    if (!telephone_item) return

    const webphone = document.getElementById('tryit-jssip-container');

    if (webphone) { // Legacy webphone
      telephone_item.click()
      if (phone_number) {
        const webphoneKeypadKeysMapper = {
          '1': 0, '2': 1, '3': 2, '4': 3, '5': 4, '6': 5,
          '7': 6, '8': 7, '9': 8, '*': 9, '0': 10, '#': 11
        };
        const number = phone_number.replace('+55', '0');
        const keypad = webphone.querySelector('.keypad.buttons');
        const backButton = webphone.querySelector('.button.back.no-sub');
        const callButton = webphone.querySelector('.button.call.no-sub');

        if (!keypad || !callButton) return;

        [...Array(15)].forEach(() => backButton.click());

        const keys = keypad.querySelectorAll('.button.digit');
        for (var i = 0; i < number.length; i++) {
          const char = number.charAt(i);
          const charIndex = webphoneKeypadKeysMapper[char];
          keys[charIndex].click();
        }
        callButton.click();
      }
    } else {
      const number = phone_number.replace('+55', '0');
      initiateCall(number);
    }
  }, [initiateCall]);

  const value = {
    // State
    phoneSignal,
    sipSignal,
    dialNumber,
    setDialNumber,
    callStatus,
    registered,
    volume,
    muted,
    widgetSignal,
    incomingData,
    uniqueid,
    callDirection,
    ctiData,
    
    // Actions
    callContact,
    incomingCall,
    initiateCall,
    endCall,
    answerCall,
    updateCallStatus,
    clearSignal,
    setPhoneSignal,
    clearSipSignal,
    setRegistered,
    setVolume,
    toggleMute,
    setCallCti,
    setWidgetSignal,
    sendDTMF,
    hold,
    unhold,
    setIncomingData,
    setUniqueid,
    setCallDirection,
    setCtiData,
  };

  return (
    <TelephonyContext.Provider value={value}>
      {children}
    </TelephonyContext.Provider>
  );
};

export default TelephonyContext;